import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Smartphone,
  Share2,
  Plus,
  Apple,
  QrCode,
  Copy,
  Download,
  Check,
  Sparkles,
} from "lucide-react";

function detectOS() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document)) return "ios";
  return "desktop";
}

export default function Install() {
  const [os, setOs] = useState("desktop");
  const [copied, setCopied] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);

  const appUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/`;
  }, []);

  useEffect(() => {
    setOs(detectOS());
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); toast.success("App installed. You can now launch it from your home screen."); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleAndroidInstall = async () => {
    if (!deferred) {
      toast("Open the ⋮ menu in your browser → tap “Install app”.");
      return;
    }
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice?.outcome === "accepted") toast.success("Installing…");
    setDeferred(null);
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(appUrl); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Could not copy — long-press the URL and copy it manually."); }
  };

  const share = async () => {
    const payload = {
      title: "NeuroOpinion India",
      text: "Book a verified neurologist / neurosurgeon second opinion from your phone.",
      url: appUrl,
    };
    if (navigator.share) {
      try { await navigator.share(payload); }
      catch (err) {
        // User cancellation throws AbortError — that's not really an error.
        if (err?.name !== "AbortError") console.warn("[share]", err?.message || err);
      }
    } else {
      copy();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="install-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Hero */}
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-50 grid place-items-center text-blue-700 shrink-0">
            <Download className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-semibold text-slate-900">Get the app</h1>
            <p className="text-sm text-slate-600 mt-1 max-w-xl leading-relaxed">
              NeuroOpinion India installs right from your browser — no app store, no download waiting.
              It works offline once installed and shows up on your home screen like any other app.
            </p>
          </div>
        </div>

        {/* Primary card */}
        <Card className="mt-6 p-6 border-slate-200" data-testid="install-primary-card">
          <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center">
            <div className="grid place-items-center bg-white rounded-2xl p-4 border border-slate-200">
              <QRCodeSVG value={appUrl} size={148} includeMargin={false} level="M" fgColor="#0f172a" />
              <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-1">
                <QrCode className="w-3 h-3" /> Scan on your phone
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Share this link</div>
              <div className="mt-1 font-mono text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 break-all" data-testid="install-url">
                {appUrl}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button onClick={copy} variant="outline" className="h-10" data-testid="install-copy-btn">
                  {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                  {copied ? "Copied" : "Copy link"}
                </Button>
                <Button onClick={share} className="h-10 bg-blue-700 hover:bg-blue-800" data-testid="install-share-btn">
                  <Share2 className="w-4 h-4 mr-1" /> Share
                </Button>
                {os === "android" && (
                  <Button onClick={handleAndroidInstall} variant="outline" className="h-10" data-testid="install-android-btn">
                    <Download className="w-4 h-4 mr-1" /> Install now
                  </Button>
                )}
              </div>
              {installed && (
                <div className="mt-3 text-sm text-emerald-700 flex items-center gap-1.5">
                  <Check className="w-4 h-4" /> Installed — check your home screen.
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Platform-specific cards */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className={`p-5 border-slate-200 ${os === "android" ? "ring-2 ring-blue-200" : ""}`} data-testid="install-android-card">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center">
                <Smartphone className="w-5 h-5" />
              </div>
              <div>
                <div className="font-heading font-semibold text-slate-900">Android</div>
                <div className="text-xs text-slate-500">Chrome, Brave, Edge, Samsung Internet</div>
              </div>
            </div>
            <ol className="mt-4 space-y-2.5 text-sm text-slate-700 list-decimal pl-5">
              <li>Open <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{appUrl}</span> in your browser.</li>
              <li>Tap the <b>⋮</b> (three-dot) menu.</li>
              <li>Tap <b>Install app</b> or <b>Add to Home screen</b>.</li>
              <li>Open it from your home screen — it runs full-screen, no browser bar.</li>
            </ol>
          </Card>

          <Card className={`p-5 border-slate-200 ${os === "ios" ? "ring-2 ring-blue-200" : ""}`} data-testid="install-ios-card">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-800 grid place-items-center">
                <Apple className="w-5 h-5" />
              </div>
              <div>
                <div className="font-heading font-semibold text-slate-900">iPhone &amp; iPad</div>
                <div className="text-xs text-slate-500">Safari only (not Chrome on iOS)</div>
              </div>
            </div>
            <ol className="mt-4 space-y-2.5 text-sm text-slate-700 list-decimal pl-5">
              <li>Open <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{appUrl}</span> in <b>Safari</b>.</li>
              <li>Tap the <Share2 className="inline w-3.5 h-3.5 -mt-0.5" /> Share button.</li>
              <li>Scroll and tap <b>Add to Home Screen <Plus className="inline w-3.5 h-3.5 -mt-0.5" /></b>.</li>
              <li>Tap <b>Add</b>. The app icon appears on your home screen.</li>
            </ol>
          </Card>
        </div>

        {/* Play Store status */}
        <Card className="mt-6 p-5 border-slate-200 bg-gradient-to-br from-blue-50/50 to-indigo-50/30" data-testid="install-stores-card">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-blue-700 mt-0.5" />
            <div>
              <div className="font-heading font-semibold text-slate-900">Coming soon to Play Store &amp; App Store</div>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                Meanwhile the installable browser version above gives you the same experience — same icon, same
                full-screen view, same offline support — and it installs in 10 seconds without a store download.
              </p>
            </div>
          </div>
        </Card>

        <div className="mt-6 text-center text-xs text-slate-500">
          Need help? <a href="mailto:support@neuroopinion.in" className="text-blue-700 font-semibold">support@neuroopinion.in</a>
          <span className="mx-2">·</span>
          <Link to="/" className="text-blue-700 font-semibold">Back to home</Link>
        </div>
      </div>
    </div>
  );
}
